import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, AlertCircle, Crown, Receipt, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useCustomPageTitle } from '../../hooks/usePageTitle';
import { stripeService } from '../../services/stripeService';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorMessage from '../../components/ui/ErrorMessage';

// ─── Design tokens ─────────────────────────────────────────────────────────────

const CARD: React.CSSProperties = {
  background: 'white',
  border: '0.5px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-card)',
  padding: '20px 24px',
};

const SECTION_LABEL: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  color: '#7C6FA0',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: 10,
  paddingBottom: 10,
  borderBottom: '0.5px solid var(--color-border)',
};

// ─── Component ─────────────────────────────────────────────────────────────────

const Billing = () => {
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useUserProfile(user?.uid);
  useCustomPageTitle('Facturation et abonnement');

  const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!user) return;
      try {
        if (profile?.subscriptionId) {
          try {
            const sub = await stripeService.getUserSubscription();
            setSubscriptionDetails(sub);
          } catch {
            // non-critical
          }
        }
      } catch {
        setError('Impossible de charger les données de facturation');
      } finally {
        setLoading(false);
      }
    };
    if (!profileLoading) load();
  }, [user, profile, profileLoading]);

  const openPortal = async () => {
    try {
      setPortalLoading(true);
      setError(null);
      const url = await stripeService.accessCustomerPortal();
      window.open(url, '_blank');
    } catch {
      setError('Impossible d\'accéder au portail de gestion');
    } finally {
      setPortalLoading(false);
    }
  };

  const formatDate = (ts: number) => {
    const d = ts > 9999999999 ? new Date(ts) : new Date(ts * 1000);
    return d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (profileLoading || loading) {
    return <div className="p-8"><LoadingSpinner /></div>;
  }

  const hasActive = profile?.subscriptionStatus === 'active' || profile?.subscriptionStatus === 'trialing';
  const credits = profile?.credits ?? 0;
  const statusLabel = hasActive
    ? (profile?.subscriptionStatus === 'trialing' ? 'Période d\'essai' : 'Actif')
    : 'Inactif';

  return (
    <div
      className="px-6 py-6 flex flex-col gap-4"
      style={{ background: 'var(--color-surface)', minHeight: '100%', fontSize: 13 }}
    >
      {error && (
        <div
          className="flex items-center gap-2"
          style={{
            padding: '8px 14px',
            background: '#FEF2F2',
            border: '0.5px solid rgba(220,38,38,0.3)',
            borderRadius: 'var(--radius-sm)',
            color: '#DC2626',
            fontSize: 12,
          }}
        >
          <AlertCircle size={13} />
          {error}
        </div>
      )}

      {/* ── Main card ── */}
      <div style={CARD}>

        {/* Header */}
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1E1148' }}>Abonnement & Crédits</span>
        </div>

        {hasActive ? (
          <>
            {/* Valorization banner */}
            <div
              className="flex items-center gap-3"
              style={{
                background: 'linear-gradient(135deg, #1E1148, #7B3FA0)',
                borderRadius: 'var(--radius-md)',
                padding: '16px 20px',
                marginBottom: 16,
              }}
            >
              <Crown size={22} color="#F97316" strokeWidth={1.75} style={{ flexShrink: 0 }} />
              <div className="flex-1">
                <p style={{ fontSize: 15, fontWeight: 500, color: '#fff', lineHeight: 1.3 }}>
                  Plan Professionnel
                </p>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 3 }}>
                  Estimations illimitées, exports PDF, accès prioritaire
                </p>
              </div>
              <span style={{
                fontSize: 11,
                fontWeight: 500,
                padding: '3px 8px',
                borderRadius: 6,
                background: 'rgba(5,150,105,0.25)',
                color: '#6EE7B7',
                flexShrink: 0,
              }}>
                Actif
              </span>
            </div>

            {/* Next billing */}
            {subscriptionDetails?.current_period_end && (
              <div style={{ ...ROW, borderTop: '0.5px solid var(--color-border)', paddingTop: 12 }}>
                <span style={SECTION_LABEL}>Prochaine facturation</span>
                <span className="flex items-center gap-1.5" style={{ color: '#1E1148' }}>
                  <Clock size={12} color="#7C6FA0" />
                  {formatDate(subscriptionDetails.current_period_end)}
                </span>
              </div>
            )}

            {subscriptionDetails?.cancel_at_period_end && (
              <div style={{ marginTop: 10, marginBottom: 4, padding: '8px 12px', background: '#FEF3C7', borderRadius: 'var(--radius-sm)', fontSize: 12, color: '#92400E' }}>
                Votre abonnement sera annulé à la fin de la période en cours.
              </div>
            )}

            {/* Action buttons */}
            <div
              className="flex items-center flex-wrap gap-3"
              style={{ marginTop: 18, paddingTop: 16, borderTop: '0.5px solid var(--color-border)' }}
            >
              <button
                onClick={openPortal}
                disabled={portalLoading}
                className="flex items-center gap-2"
                style={{
                  background: 'var(--gradient-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: portalLoading ? 'not-allowed' : 'pointer',
                  opacity: portalLoading ? 0.7 : 1,
                }}
              >
                <Receipt size={13} />
                {portalLoading ? 'Redirection…' : 'Mes factures'}
              </button>

              <button
                onClick={openPortal}
                disabled={portalLoading}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 12,
                  color: 'rgb(var(--color-primary))',
                  cursor: portalLoading ? 'not-allowed' : 'pointer',
                  padding: 0,
                }}
              >
                Gérer l'abonnement →
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Credits big */}
            <div style={{ marginBottom: 16 }}>
              <p style={SECTION_LABEL}>Crédits disponibles</p>
              <p style={{ fontSize: 48, fontWeight: 700, lineHeight: 1, color: 'rgb(var(--color-primary))', marginTop: 4 }}>
                {credits}
              </p>
              <p style={{ fontSize: 12, color: '#7C6FA0', marginTop: 6 }}>
                {credits > 0
                  ? `Vous pouvez estimer ${credits} adresse${credits > 1 ? 's' : ''} différente${credits > 1 ? 's' : ''}`
                  : 'Aucun crédit disponible'
                }
              </p>
            </div>

            <div
              className="flex items-center gap-4 flex-wrap"
              style={{ paddingTop: 16, borderTop: '0.5px solid var(--color-border)' }}
            >
              <Link
                to="/credits/purchase"
                className="btn btn-primary"
                style={{ fontSize: 12, padding: '6px 16px', background: 'var(--gradient-accent)', border: 'none' }}
              >
                + Acheter des crédits
              </Link>

              <Link
                to="/credits/purchase"
                className="btn"
                style={{
                  fontSize: 12,
                  padding: '6px 16px',
                  background: 'var(--gradient-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                Souscrire à l'abonnement
              </Link>
            </div>
          </>
        )}
      </div>

      {/* ── Contact card ── */}
      <div style={CARD}>
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1E1148' }}>Besoin d'aide ?</span>
        </div>
        <p style={{ fontSize: 13, color: '#7C6FA0', lineHeight: 1.6, marginBottom: 16 }}>
          Pour toute question sur votre abonnement ou vos factures, contactez-nous.
        </p>
        <a
          href="mailto:support@simulreno.com"
          className="inline-flex items-center gap-2"
          style={{
            background: 'var(--gradient-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            padding: '8px 16px',
            fontSize: 12,
            fontWeight: 500,
            textDecoration: 'none',
          }}
        >
          <Mail size={13} />
          Nous contacter
        </a>
      </div>
    </div>
  );
};

export default Billing;
